import { Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from "@angular/core";
import { forkJoin, Subject } from "rxjs";
import {
  MeterDataRefreshOptions,
  MeterProviderService,
  MeterStreamHistoryRange
} from "@shared/services/api/meter-provider.service";
import { AceLoggerService } from "@logging/ace-logger.service";
import { AceLogger } from "@logging/ace-logger";
import { Meter, MeterStream, MeterType } from "@app/graphql-typings/graphql";
import { finalize, takeUntil } from "rxjs/operators";
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import {
  DateSelectComponentFields,
  DateTimeRange
} from "@shared/components/date-time-range-select/date-time-range-select.component";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { MetersCache } from "@shared/data-stores/meters-cache";
import { IntervalDurationType, PeriodUtilService } from "@shared/services/util/period-util.service";
import { MetersService } from "@app/layout/sitemanager/meters/meters.service";
import { ConfirmationComponent } from "@shared/components/confirmation/confirmation.component";
import { AppConstants } from "@shared/util/app-constants";
import { I18nMessage } from "@shared/api/model/i18n";
import { ToasterService } from "@shared/components/toaster/toaster.service";
import { ToastType } from "@shared/components/toaster/Toast";
import { SiteService } from "@shared/services";


enum RangeType {
  WHOLE_PERIOD,
  SPECIFY_PERIOD
}


@Component({
  selector: 'app-recalculate-meterdata-button',
  templateUrl: './recalculate-meterdata-button.component.html',
  styleUrls: ['./recalculate-meterdata-button.component.scss'],
  standalone: false
})
export class RecalculateMeterdataButtonComponent implements OnChanges, OnDestroy {

  private readonly logger: AceLogger;
  private readonly ngUnsubscribe: Subject<void> = new Subject();
  private readonly DISABLED_NO_ACTIVE_SITE_MESSAGE: I18nMessage = {
    messageKey: 'No active site associated with current meter'
  };

  @Input() siteId: number;
  @Input() meterStream: MeterStream;
  @Input() buttonStyles: string | string[] | Set<string> | { [klass: string]: any; };
  @Input() showText = true;

  @ViewChild('recalculateRangePicker', { static: false })
  recalculateRangePicker: ElementRef;

  readonly RangeType = RangeType;
  readonly RANGE_TYPE = 'rangeType';
  readonly RANGE = 'range';

  showTimePicker = false;
  recalculateRangePickerModal: NgbModalRef;

  refreshRangeForm: UntypedFormGroup;

  recalculating = false;
  submitting = false;
  _meterDataRange: MeterStreamHistoryRange;
  _recalculationRange: MeterStreamHistoryRange;

  recalculationRangeStart: string;
  recalculationRangeEnd: string;

  meterDataRangeStart: string;
  meterDataRangeEnd: string;

  timezone: string;
  disabled = false;
  disabledMessage: I18nMessage | null = null;

  constructor(private meterProviderService: MeterProviderService,
              private modalService: NgbModal,
              private metersCache: MetersCache,
              private periodUtil: PeriodUtilService,
              private metersService: MetersService,
              private siteService: SiteService,
              private toasterService: ToasterService,
              formBuilder: UntypedFormBuilder,
              logService: AceLoggerService) {
    this.logger = logService.create(RecalculateMeterdataButtonComponent.name);
    this.setupRecalculationRangeForm(formBuilder);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.meterStream || changes?.siteId) {
      if (this.meterStream == null) {
        throw new Error('Recalculate Meter Data Button cannot function without meter or meter stream.');
      }
      this.metersCache.getMetersBySiteIds([this.siteId]).subscribe(metersById => {
        const meter = metersById.get(this.meterStream.nmi_id);
        this.meterStream.nmi = meter;
        this.updateShowTimePicker(meter, metersById);
        this.updateTimezoneOrDisable(meter);
      });
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  recalculate() {
    this.recalculating = true;
    forkJoin([
      this.meterProviderService.getMeterDataHistoryRangeForMeterStream(this.meterStream),
      this.meterProviderService.getMeterDataSourceHistoryRangeForMeterStream(this.meterStream)
    ]).pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => this.recalculating = false))
      .subscribe(([historyRange, sourceHistoryRange]) => {
        if (sourceHistoryRange == null) {
          this.showSourceHistoryRangeEmptyError();
        } else {
          this.showRecalculationRangePicker(historyRange, sourceHistoryRange);
        }
      });
  }

  submitRecalculation() {
    this.submitting = true;
    const recalculationRange: DateTimeRange = this.refreshRangeForm.get(this.RANGE).value;
    const params: MeterDataRefreshOptions = this.refreshRangeForm.get(this.RANGE_TYPE).value === RangeType.WHOLE_PERIOD
      ? { fill_ts_range: true }
      : {
        start_ts: recalculationRange[DateSelectComponentFields.START_DATE],
        end_ts: recalculationRange[DateSelectComponentFields.END_DATE]
      };
    this.meterProviderService.recalculatingMeterStreamData(this.meterStream.nmi_id, this.meterStream.stream_id, params)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => {
          this.submitting = false;
          this.recalculateRangePickerModal.close();
        }))
      .subscribe(() =>
        this.toasterService.push({
          type: ToastType.Success,
          dismissible: true,
          message: { messageKey: 'Meter data recalculation has been submitted' }
        })
      );
  }

  asModal(modalRef: NgbModalRef): NgbModalRef {
    return modalRef;
  }

  get recalculationRange(): MeterStreamHistoryRange {
    return this._recalculationRange;
  }

  get meterDataRange(): MeterStreamHistoryRange {
    return this._meterDataRange;
  }

  private updateShowTimePicker(meter: Meter, metersById: Map<number, Meter>) {
    if (meter.meter_type === MeterType.VIRTUAL) {
      const intervalDuration = this.metersService.calculateVirtualMeterStreamInterval(meter, metersById);
      this.showTimePicker = !!intervalDuration?.toISOString().startsWith('PT');
    } else {
      this.showTimePicker = this.periodUtil.getIntervalDurationType(meter.data_interval) === IntervalDurationType.PT;
    }
  }

  private updateTimezoneOrDisable(meter: Meter) {
    this.disabled = false;
    this.disabledMessage = null;
    if (meter.site_id != null) {
      this.siteService.getCachedSiteInfo(meter.site_id).subscribe(site => {
        if (site != null) {
          this.timezone = site.timezone;
        } else {
          this.disabled = true;
          this.disabledMessage = this.DISABLED_NO_ACTIVE_SITE_MESSAGE;
        }
      });
    } else {
      this.disabled = true;
      this.disabledMessage = this.DISABLED_NO_ACTIVE_SITE_MESSAGE;
    }
  }

  private setupRecalculationRangeForm(formBuilder: UntypedFormBuilder) {
    const rangeTypeControl = new UntypedFormControl(RangeType.WHOLE_PERIOD);
    const rangeControl = new UntypedFormControl({ value: null, disabled: true }, Validators.required);
    this.refreshRangeForm = formBuilder.group({
      [this.RANGE_TYPE]: rangeTypeControl,
      [this.RANGE]: rangeControl
    });

    rangeTypeControl.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(rangeType => {
      if (rangeType === RangeType.WHOLE_PERIOD) {
        rangeControl.disable({ emitEvent: false });
      } else {
        rangeControl.enable({ emitEvent: false });
      }
    });
  }

  private showSourceHistoryRangeEmptyError() {
    const errorBodyMessageKey = this.meterStream.nmi.meter_type === MeterType.VIRTUAL
      ? 'Cannot recalculate meter data for stream as related meters have no data'
      : 'Cannot recalculate meter data for stream as source favourites have no data';
    ConfirmationComponent.createModal(this.modalService, {
      title: {
        messageKey: 'Cannot recalculate meter data'
      },
      body: {
        messageKey: errorBodyMessageKey,
        parameters: {
          nmiNumber: this.meterStream.nmi_number,
          streamId: this.meterStream.stream_id
        }
      },
      hideCancelButton: true,
      yesText: { messageKey: 'OK' }
    });
  }

  private set recalculationRange(range: MeterStreamHistoryRange) {
    this._recalculationRange = range;
    if (range) {
      const format = this.showTimePicker ? AppConstants.TIME_FORMAT_SECOND_PRECISION : AppConstants.DATE_FORMAT;
      this.recalculationRangeStart = range.start_ts.clone().tz(this.timezone).format(format);
      this.recalculationRangeEnd = range.end_ts.clone().tz(this.timezone).format(format);
    }
  }

  private set meterDataRange(range: MeterStreamHistoryRange) {
    this._meterDataRange = range;
    if (range) {
      const format = this.showTimePicker ? AppConstants.TIME_FORMAT_SECOND_PRECISION : AppConstants.DATE_FORMAT;
      this.meterDataRangeStart = range.start_ts.clone().tz(this.timezone).format(format);
      this.meterDataRangeEnd = range.end_ts.clone().tz(this.timezone).format(format);
    }
  }

  private showRecalculationRangePicker(currentMeterHistoryRange: MeterStreamHistoryRange, sourceHistoryRange: MeterStreamHistoryRange) {
    this.meterDataRange = currentMeterHistoryRange;
    this.recalculationRange = sourceHistoryRange;

    this.refreshRangeForm.get(this.RANGE).setValue({
      [DateSelectComponentFields.START_DATE]: sourceHistoryRange.start_ts,
      [DateSelectComponentFields.END_DATE]: sourceHistoryRange.end_ts,
    });
    this.recalculateRangePickerModal = this.modalService.open(this.recalculateRangePicker, { centered: true });
    this.recalculateRangePickerModal.result.finally(() => this.recalculateRangePickerModal = null);
  }
}
